/* Page state for the notification list */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { NOTIFICATIONS_PER_PAGE, NotificationPage, paginateNotifications } from './notificationPagination';

/**
 * Tracks the selected page and derives the visible slice.
 *
 * `resetToFirstPage` is exposed so the caller can return to the top when the panel
 * reopens — otherwise a user who paged to the end would reopen mid-list.
 */
export const useNotificationPagination = <T>(items: T[], pageSize: number = NOTIFICATIONS_PER_PAGE) => {
  const [requestedPage, setRequestedPage] = useState(1);

  const page: NotificationPage<T> = useMemo(
    () => paginateNotifications(items, requestedPage, pageSize),
    [items, requestedPage, pageSize]
  );

  // Keep the stored page in step with the clamped one, so the control's selected page
  // matches what is rendered after the list shrinks.
  useEffect(() => {
    if (page.currentPage !== requestedPage) {
      setRequestedPage(page.currentPage);
    }
  }, [page.currentPage, requestedPage]);

  const resetToFirstPage = useCallback(() => setRequestedPage(1), []);

  return {
    page,
    selectPage: setRequestedPage,
    resetToFirstPage
  };
};
